// src/components/SnapshotFinding.js
//
// Presentational component for the compact Snapshot page (ticket
// 023-snapshotredesign). One Finding = a numbered title + 1-3 sentence body,
// optionally a compact income-classification table or a "read the full
// guide" link-out. All content comes pre-built from
// src/lib/buildSnapshotFindings.js — this component does no fact-reading or
// branching of its own beyond "does this finding have a table/link-out."
//
// New trc-* CSS namespace per D9 — not a reuse of tr-*/tbe-*.
//
// Content-review pass (023, item 5): a body[] entry is either a plain string
// (rendered as-is) or an inline-link object { pre, href, linkText, post },
// rendered as pre + <a>linkText</a> + post within the same <p>. This is a
// plain inline text link mid-sentence — a different, smaller thing than
// finding.linkOut below (a standalone "read the guide" line) — neither
// duplicates ResultCTA's own primary CTA in the sidebar.
//
// D2 (ticket 036): a body[] entry can also be a third shape,
// { type: 'bulletList', lead: string, items: string[] } — renders `lead` as
// a plain <p className="trc-finding-body">, then `items` as a
// <ul className="trc-finding-bullet-list"> of <li className="trc-finding-
// bullet"> (plain strings only, not the inline-link shape).
//
// D1 (ticket 036): the Finding object can also carry a top-level
// leadSentence field (same shape as a plain-string/inline-link body entry),
// rendered via FindingSentence immediately after the title and before
// finding.table. Only buildIncomeTableFinding sets it today. Render order:
// title → leadSentence → table → body → linkOut.
//
// Ticket 069: ONE optional prop, openLinksInNewTab (default off). When on, the
// inline links and the linkOut link open in a new tab (the Advisor Intake results
// page uses it so the client does not lose their place). Off, the markup is exactly
// what it was; TriageResults does not pass it.

import React from 'react'
import { BASE_LABELS } from './TaxBaseExplainer'

const NEW_TAB_PROPS = { target: '_blank', rel: 'noopener noreferrer' }

function FindingSentence({ sentence, linkProps = {} }) {
  if (typeof sentence === 'string') return sentence
  return (
    <>
      {sentence.pre}
      <a className="trc-inline-link" href={sentence.href} {...linkProps}>{sentence.linkText}</a>
      {sentence.post}
    </>
  )
}

function SnapshotIncomeTable({ table }) {
  const { rows, presentBases, rateText } = table
  return (
    <div className="trc-income-table">
      <div className={`trc-income-cols trc-income-cols--${presentBases.length}`}>
        {presentBases.map(base => (
          <div className={`trc-income-col trc-income-col--${base}`} key={base}>
            <div className="trc-income-col-head">{(BASE_LABELS[base] || base).toUpperCase()}</div>
            <div className="trc-income-col-rate">{rateText[base]}</div>
            <ul className="trc-income-col-list">
              {rows.filter(row => row.base === base).map(row => (
                <li className="trc-income-row" key={row.id}>
                  <span>{row.label}</span>
                  {row.eventNote && <span className="trc-income-eventnote">({row.eventNote})</span>}
                  {row.confidence && row.confidence !== 'high' && (
                    <span className="trc-review-tag">Review</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function SnapshotFinding({ index, finding, openLinksInNewTab = false }) {
  const linkProps = openLinksInNewTab ? NEW_TAB_PROPS : {}
  return (
    <div className="trc-finding">
      <div className="trc-finding-title">
        <span className="trc-finding-number">{index}</span>
        {finding.title}
      </div>
      {finding.leadSentence && (
        <p className="trc-finding-body"><FindingSentence sentence={finding.leadSentence} linkProps={linkProps} /></p>
      )}
      {finding.table && <SnapshotIncomeTable table={finding.table} />}
      {finding.body.map((sentence, i) => {
        if (typeof sentence === 'object' && sentence.type === 'bulletList') {
          return (
            <React.Fragment key={i}>
              <p className="trc-finding-body">{sentence.lead}</p>
              <ul className="trc-finding-bullet-list">
                {sentence.items.map((item, j) => (
                  <li className="trc-finding-bullet" key={j}>{item}</li>
                ))}
              </ul>
            </React.Fragment>
          )
        }
        return (
          <p className="trc-finding-body" key={i}><FindingSentence sentence={sentence} linkProps={linkProps} /></p>
        )
      })}
      {finding.linkOut && (
        <a className="trc-finding-link" href={finding.linkOut.href} {...linkProps}>{finding.linkOut.label}</a>
      )}
    </div>
  )
}
